import {
  ClientsInput,
  DeleteTemplateMutation,
  GetQueriesQuery,
  GetTemplateByIdQuery,
  ListTemplateResponse,
  ListTemplates_v2Query,
  SaveTemplates_v2Mutation,
  TemplatePlaceholder,
  UpdateTemplate_v2Mutation,
  mediaSource,
} from 'API';
import { API } from 'aws-amplify';
import { deleteTemplate, saveTemplates_v2, updateTemplate_v2 } from 'graphql/mutations';
import { getQueries, getTemplateById, listTemplates_v2 } from 'graphql/queries';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import APIContext from './APIContext';
import { useCampaignContext } from './CampaignContext';
import { useClientContext } from './ClientContext';
import { useGetStoredQueriesDetails } from 'customHooks/useGetStoredQueriesDetails';
import { useDragAndDrop } from 'customHooks/useDragAndDrop';
import AuthContext from './AuthContext';

type TemplateContextProps = {
  templateSave: (args: any) => void;
  updateTemplatePlaceholderList: (args: any) => void;
  fetchTemplateById: (args: any) => void;
  templateDelete: (args: any) => void;
  templatesList: any[];
  fetchedPlaceholders: TemplatePlaceholder[];
  saveTemplateMessage: string;
  templateName: string;
  setTemplateName: (args: any) => void;
  fetchedTemplateData: fetchedTemplateData;
  templateUpdate: () => void;
  toggleDrag: () => void;
  isDragEnabled: boolean;
  /*     fetchedTemplateData: {
            Name: null,
            Id: null,
            CreatedBy: null,
            CreatedAt: null,
            LastEditedBy: null,
            LastEditedAt: null,
        } */
};

export const TemplateContext = createContext<TemplateContextProps | undefined>(undefined);

type fetchedTemplateData =
  | {
      Name: string;
      Id: any;
    }
  | undefined;

interface Metric {
  chartType: string;
  metric: string;
  axis: number;
}

export const TemplateContextProvider = (props: any) => {
  const { activeCampaign, dashType } = useCampaignContext();
  const { activeOrganization } = useClientContext();
  const { getEnvironment } = useContext(APIContext);
  const { testUser } = useContext(AuthContext);
  const [templatesList, setTemplatesList] = useState<any>([]);
  // const placeholders: any[] = [];
  const [placeholders, setPlaceholders] = useState<any>([]);
  const [fetchedPlaceholders, setFetchedPlaceholders] = useState<TemplatePlaceholder[]>([]);
  const [saveTemplateMessage, setSaveTemplateMessage] = useState<any>();
  const [templateName, setTemplateName] = useState<string>('');
  const [fetchedTemplateData, setFetchedTemplateData] = useState<fetchedTemplateData>();
  const { REACT_APP_AGENCY } = process.env;
  const [
    storedQueries,
    setStoredQueries,
    tablePlaceholderList,
    setTablePlaceholderList,
    setGraphPlaceholderList,
    graphPlaceholderList,
    publishedQueries,
  ] = useGetStoredQueriesDetails(activeCampaign, undefined, fetchedPlaceholders);
  const dashboardTypeForAPI = dashType === 'SEARCH' ? mediaSource.SEARCH : mediaSource.DIGITAL;

  const { getOrderList } = useDragAndDrop({ tablePlaceholderList, dashboardType: dashboardTypeForAPI });

  useEffect(() => {
    if (activeCampaign) {
      setTemplatesList([]);
      setPlaceholders([]);
      setFetchedPlaceholders([]);
      setSaveTemplateMessage(null);
      setTemplateName('');
      setFetchedTemplateData(undefined);

      activeOrganization !== undefined && fetchTemplateList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCampaign]);

  useEffect(() => {
    if (saveTemplateMessage) {
      setTimeout(() => {
        setSaveTemplateMessage(undefined);
      }, 2000);
    }
  }, [saveTemplateMessage]);

  // Nova funkcija za dohvaćanje TV/BVOD template podataka
  const fetchTVTemplates = useCallback(async () => {
    setStoredQueries([]);
    setFetchedPlaceholders([]);

    if (!activeCampaign?.dashboardType) return;

    const hasTV = activeCampaign.dashboardType.includes('TV' as any);
    const hasBVOD = activeCampaign.dashboardType.includes('BVOD' as any);

    try {
      let allPlaceholders: TemplatePlaceholder[] = [];
      let templateName = '';
      const tvTemplateID =
        activeOrganization?.OrganizationId === 'STAB#NO' /*  && testUser */
          ? 'c95f512f-a597-4fcd-aacd-db50b34bf754'
          : 'c9614b9a-ed63-4a1c-9227-7d2c62247469';
      const bvodTemplateID =
        activeOrganization?.OrganizationId === 'STAB#NO'
          ? 'f2fb812c-cdda-42c0-98eb-b108ec03b8a9'
          : '2dff675a-8b1f-4765-9cf4-86c8a0a8df44';

      // Ako ima i TV i BVOD, pozovi oba API poziva
      if (hasTV && hasBVOD) {
        const [tvResponse, bvodResponse] = await Promise.all([
          API.graphql({
            query: getTemplateById,
            variables: {
              TemplateId: tvTemplateID, // TV template ID
              Client: {
                ClientCode: activeCampaign?.ClientCode,
                ClientCountry: activeCampaign?.ClientCountry,
              } as ClientsInput,
            },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          }),
          API.graphql({
            query: getTemplateById,
            variables: {
              TemplateId: bvodTemplateID, // BVOD template ID
              Client: {
                ClientCode: activeCampaign?.ClientCode,
                ClientCountry: activeCampaign?.ClientCountry,
              } as ClientsInput,
            },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          }),
        ]);

        // Spoji placeholders iz oba template-a
        const tvData = (tvResponse as { data: GetTemplateByIdQuery }).data;
        const bvodData = (bvodResponse as { data: GetTemplateByIdQuery }).data;

        const tvPlaceholders =
          (tvData.getTemplateById?.Placeholders?.filter((p) => p !== null) as TemplatePlaceholder[]) || [];
        const bvodPlaceholders =
          (bvodData.getTemplateById?.Placeholders?.filter((p) => p !== null) as TemplatePlaceholder[]) || [];
        allPlaceholders = [...tvPlaceholders, ...bvodPlaceholders];

        // Koristi naziv prvog template-a ili kombiniranu verziju
        templateName = tvData.getTemplateById?.Name || 'TV & BVOD Template';

        // Postavi template data na osnovu prvog (TV) template-a
        if (tvData.getTemplateById) {
          const { Name, Id } = tvData.getTemplateById;
          const newTemplate = { Name: Name ?? 'unknown', Id: Id ?? 'unknown' };
          setFetchedTemplateData(newTemplate);
        }
      }
      // Ako ima samo TV
      else if (hasTV && !hasBVOD) {
        const response = (await API.graphql({
          query: getTemplateById,
          variables: {
            TemplateId: tvTemplateID, // TV template ID
            Client: {
              ClientCode: activeCampaign?.ClientCode,
              ClientCountry: activeCampaign?.ClientCountry,
            } as ClientsInput,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetTemplateByIdQuery };

        allPlaceholders =
          (response.data.getTemplateById?.Placeholders?.filter((p) => p !== null) as TemplatePlaceholder[]) || [];
        templateName = response.data.getTemplateById?.Name || 'TV Template';

        if (response.data.getTemplateById) {
          const { Name, Id } = response.data.getTemplateById;
          const newTemplate = { Name: Name ?? 'unknown', Id: Id ?? 'unknown' };
          setFetchedTemplateData(newTemplate);
        }
      }
      // Ako ima samo BVOD
      else if (!hasTV && hasBVOD) {
        const response = (await API.graphql({
          query: getTemplateById,
          variables: {
            TemplateId: bvodTemplateID, // BVOD template ID
            Client: {
              ClientCode: activeCampaign?.ClientCode,
              ClientCountry: activeCampaign?.ClientCountry,
            } as ClientsInput,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetTemplateByIdQuery };

        allPlaceholders =
          (response.data.getTemplateById?.Placeholders?.filter((p) => p !== null) as TemplatePlaceholder[]) || [];
        templateName = response.data.getTemplateById?.Name || 'BVOD Template';

        if (response.data.getTemplateById) {
          const { Name, Id } = response.data.getTemplateById;
          const newTemplate = { Name: Name ?? 'unknown', Id: Id ?? 'unknown' };
          setFetchedTemplateData(newTemplate);
        }
      }

      // Postavi konačne rezultate
      if (templateName) {
        setTemplateName(templateName);
      }

      if (allPlaceholders.length > 0) {
        setFetchedPlaceholders(allPlaceholders);
      }
    } catch (e) {
      console.log('ERROR FETCHING TV/BVOD TEMPLATES', e);
    }
  }, [activeCampaign, setStoredQueries, activeOrganization]);

  useEffect(() => {
    setFetchedPlaceholders([]);
    if (activeOrganization) {
      if (activeCampaign && dashType === 'TV') {
        fetchTVTemplates();
      } else if (activeCampaign && dashType === 'RADIO') {
        if (activeOrganization.OrganizationId === 'STAB#NO' /* && testUser */) {
          fetchTemplateById('398412c7-ca0c-4842-8187-1be62640eae7');
        } else {
          fetchTemplateById('26126d4e-f347-4441-b36c-7ab7d3598f47');
        }
      } else if (activeCampaign && dashType === 'OOH') {
        fetchTemplateById('7974c060-bad2-4048-9ce8-f9dbcc1a0753');
      } else if (activeCampaign && dashType === 'PRINT') {
        fetchTemplateById('fd723399-b637-408c-a77a-d69104022dde');
      } else if (activeCampaign && dashType === 'CINEMA') {
        fetchTemplateById('fd723399-b637-408c-a77a-kdfkgmsdfkgmsdf');
      }
    }
  }, [activeCampaign, dashType, fetchTVTemplates, activeOrganization]);

  const updateTemplatePlaceholderList = (locationObject: any) => {
    if (dashType === 'TV' || dashType === 'RADIO') {
      delete locationObject.metrics;
    } else {
      if (locationObject.metrics) {
        locationObject.metrics.forEach((e: any) => {
          delete e.__typename;
        });
      }
    }

    const index = placeholders.findIndex((e: any) => e.Placeholder === locationObject.Placeholder);
    if (index === -1) {
      setPlaceholders((ps: any) => [...ps, locationObject]);
    } else {
      const newArray = [...placeholders];
      newArray.splice(index);
      newArray.push(locationObject);
      setPlaceholders(newArray);
    }
    //    console.log(placeholders)
  };

  async function templateSave(name: any) {
    try {
      const response = (await API.graphql({
        query: getQueries,
        variables: {
          campaign_id: activeCampaign?.campaignId?.toString(),
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetQueriesQuery };
      const storedQueries = response.data.getQueries;
      const fetchedTableOrder = await getOrderList('Table');
      const fetchedChartOrder = await getOrderList('Chart');
      const orderListBenchmark = fetchedTableOrder?.concat(
        fetchedChartOrder?.map((e) => {
          return {
            ...e,
            Order: fetchedTableOrder?.length + e.Order,
          };
        }) as any,
      );

      storedQueries?.forEach((e) => {});

      let filteredQueries;
      if (dashType === 'DIGITAL') {
        // For DIGITAL dashboard: exclude Search positions and standard exclusions
        filteredQueries = storedQueries?.filter(
          (e) =>
            !e?.position?.startsWith('Summary') &&
            !e?.position?.startsWith('Brief') &&
            !e?.position?.startsWith('3') &&
            !e?.position?.startsWith('4') &&
            !e?.position?.startsWith('5') &&
            !e?.position?.startsWith('OOH') &&
            !e?.position?.startsWith('Print') &&
            !e?.position?.startsWith('Radio') &&
            !e?.position?.startsWith('TV') &&
            !e?.position?.startsWith('Cinema') &&
            !e?.position?.startsWith('Plan') &&
            !e?.position?.startsWith('BVOD') &&
            !e?.position?.startsWith('Search'),
        );
      } else if (dashType === 'SEARCH') {
        // For SEARCH dashboard: only include Search positions, but exclude Search3, Search4, Search5 and all standard exclusions
        filteredQueries = storedQueries?.filter(
          (e) =>
            !e?.position?.startsWith('Summary') &&
            !e?.position?.startsWith('Brief') &&
            !e?.position?.startsWith('3') &&
            !e?.position?.startsWith('4') &&
            !e?.position?.startsWith('5') &&
            !e?.position?.startsWith('OOH') &&
            !e?.position?.startsWith('Print') &&
            !e?.position?.startsWith('Radio') &&
            !e?.position?.startsWith('TV') &&
            !e?.position?.startsWith('Cinema') &&
            !e?.position?.startsWith('Plan') &&
            !e?.position?.startsWith('BVOD') &&
            e?.position?.startsWith('Search') &&
            !e?.position?.startsWith('Search3') &&
            !e?.position?.startsWith('Search4') &&
            !e?.position?.startsWith('Search5'),
        );
      } else {
        // Fallback to original logic for other dashboard types
        filteredQueries = storedQueries?.filter(
          (e) =>
            !e?.position?.startsWith('Summary') &&
            !e?.position?.startsWith('Brief') &&
            !e?.position?.startsWith('3') &&
            !e?.position?.startsWith('4') &&
            !e?.position?.startsWith('5') &&
            !e?.position?.startsWith('OOH') &&
            !e?.position?.startsWith('Print') &&
            !e?.position?.startsWith('Radio') &&
            !e?.position?.startsWith('TV') &&
            !e?.position?.startsWith('Cinema') &&
            !e?.position?.startsWith('Plan') &&
            !e?.position?.startsWith('BVOD'),
        );
      }

      const placeholders = storedQueries?.map((e) => {
        if (e?.query_id_cc) {
          return {
            Location: e?.query_id,
            Location_cc: e?.query_id_cc,
            Placeholder: e?.position,
            Placeholder_titel: e?.title,
            OrderIndex: orderListBenchmark?.find((el: any) => el.QueryId === e?.position)?.Order ?? null,
            metrics: e?.metrics?.reduce<Metric[]>((acc, current) => {
              const x = acc.findIndex(
                (item) =>
                  item.chartType === current?.chartType && item.metric === current.metric && item.axis === current.axis,
              );
              if (x <= -1) {
                const obj = { metric: current?.metric, chartType: current?.chartType, axis: current?.axis };
                acc.push(obj as Metric);
              }
              return acc;
            }, []),
          };
        } else {
          return {
            Location: e?.query_id,
            Placeholder: e?.position,
            Placeholder_titel: e?.title,
            OrderIndex: orderListBenchmark?.find((el: any) => el.QueryId === e?.position)?.Order ?? null,
            metrics: e?.metrics?.reduce<Metric[]>((acc, current) => {
              const x = acc.findIndex(
                (item) =>
                  item.chartType === current?.chartType && item.metric === current.metric && item.axis === current.axis,
              );
              if (x <= -1) {
                const obj = { metric: current?.metric, chartType: current?.chartType, axis: current?.axis };
                acc.push(obj as Metric);
              }
              return acc;
            }, []),
          };
        }
      });
      if (placeholders) {
        saveTemplateAfeterCallingGetStoredQ(name, placeholders);
      }
    } catch (e) {
      console.log('ERROR FETCHING QUARIES', e);
    }
  }

  async function saveTemplateAfeterCallingGetStoredQ(name: any, placeholders: any) {
    try {
      const response = (await API.graphql({
        query: saveTemplates_v2,
        variables: {
          ClientCode: activeCampaign?.ClientCode,
          ClientCountry: activeCampaign?.ClientCountry,
          Environment: getEnvironment,
          TemplateName: name,
          Placeholders: placeholders,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: SaveTemplates_v2Mutation };
      setSaveTemplateMessage(response.data.saveTemplates_v2?.Message);
      fetchTemplateList();
    } catch (e) {
      console.log('ERROR SAVING TEMPLATE', e);
    }
  }

  async function templateUpdate() {
    try {
      const response = (await API.graphql({
        query: getQueries,
        variables: {
          campaign_id: activeCampaign?.campaignId?.toString(),
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: GetQueriesQuery };
      const storedQueries = response.data.getQueries;
      const fetchedTableOrder = await getOrderList('Table');
      const fetchedChartOrder = await getOrderList('Chart');
      const orderListBenchmark = fetchedTableOrder?.concat(
        fetchedChartOrder?.map((e) => {
          return {
            ...e,
            Order: fetchedTableOrder?.length + e.Order,
          };
        }) as any,
      );

      storedQueries?.forEach((e) => {});

      let filteredQueries;
      if (dashType === 'DIGITAL') {
        // For DIGITAL dashboard: exclude Search positions and standard exclusions
        filteredQueries = storedQueries?.filter(
          (e) =>
            !e?.position?.startsWith('Summary') &&
            !e?.position?.startsWith('Brief') &&
            !e?.position?.startsWith('3') &&
            !e?.position?.startsWith('4') &&
            !e?.position?.startsWith('5') &&
            !e?.position?.startsWith('OOH') &&
            !e?.position?.startsWith('Print') &&
            !e?.position?.startsWith('Radio') &&
            !e?.position?.startsWith('TV') &&
            !e?.position?.startsWith('Cinema') &&
            !e?.position?.startsWith('Plan') &&
            !e?.position?.startsWith('BVOD') &&
            !e?.position?.startsWith('Search'),
        );
      } else if (dashType === 'SEARCH') {
        // For SEARCH dashboard: only include Search positions, but exclude Search3, Search4, Search5 and all standard exclusions
        filteredQueries = storedQueries?.filter(
          (e) =>
            !e?.position?.startsWith('Summary') &&
            !e?.position?.startsWith('Brief') &&
            !e?.position?.startsWith('3') &&
            !e?.position?.startsWith('4') &&
            !e?.position?.startsWith('5') &&
            !e?.position?.startsWith('OOH') &&
            !e?.position?.startsWith('Print') &&
            !e?.position?.startsWith('Radio') &&
            !e?.position?.startsWith('TV') &&
            !e?.position?.startsWith('Cinema') &&
            !e?.position?.startsWith('Plan') &&
            !e?.position?.startsWith('BVOD') &&
            e?.position?.startsWith('Search') &&
            !e?.position?.startsWith('Search3') &&
            !e?.position?.startsWith('Search4') &&
            !e?.position?.startsWith('Search5'),
        );
      } else {
        // Fallback to original logic for other dashboard types
        filteredQueries = storedQueries?.filter(
          (e) => !e?.position?.startsWith('Summary') && !e?.position?.startsWith('Brief'),
        );
      }

      const placeholders = filteredQueries?.map((e) => {
        if (e?.query_id_cc) {
          return {
            Location: e?.query_id,
            Location_cc: e?.query_id_cc,
            Placeholder: e?.position,
            Placeholder_titel: e?.title,
            OrderIndex: orderListBenchmark?.find((el: any) => el.QueryId === e?.position)?.Order ?? null,
            metrics: e?.metrics?.reduce<Metric[]>((acc, current) => {
              const x = acc.findIndex(
                (item) =>
                  item.chartType === current?.chartType && item.metric === current.metric && item.axis === current.axis,
              );
              if (x <= -1) {
                const obj = { metric: current?.metric, chartType: current?.chartType, axis: current?.axis };
                acc.push(obj as Metric);
              }
              return acc;
            }, []),
          };
        } else {
          return {
            Location: e?.query_id,
            Placeholder: e?.position,
            Placeholder_titel: e?.title,
            OrderIndex: orderListBenchmark?.find((el: any) => el.QueryId === e?.position)?.Order ?? null,
            metrics: e?.metrics?.reduce<Metric[]>((acc, current) => {
              const x = acc.findIndex(
                (item) =>
                  item.chartType === current?.chartType && item.metric === current.metric && item.axis === current.axis,
              );
              if (x <= -1) {
                const obj = { metric: current?.metric, chartType: current?.chartType, axis: current?.axis };
                acc.push(obj as Metric);
              }
              return acc;
            }, []),
          };
        }
      });
      if (placeholders) {
        templateUpdateAfterCAllingStoredQ(templateName, placeholders);
      }
    } catch (e) {
      console.log('ERROR FETCHING QUARIES', e);
    }
  }

  const getCounrty = activeOrganization?.OrganizationId.split('#')[1];
  const countriesList = [
    { name: 'Sweden', code: 'SE' },
    { name: 'Norway', code: 'NO' },
    { name: 'Denmark', code: 'DK' },
    { name: 'Finland', code: 'FI' },
    { name: 'Global', code: 'GLOBAL' },
  ];
  const country = countriesList.find((e) => e.code === getCounrty)?.name;
  const agencyTemplate = {
    Name: `${REACT_APP_AGENCY?.toUpperCase()} ${country} Template`,
    Id: '72d402eb-26c4-4f1e-81c4-62b58ba811a5',
    CreatedBy: `${REACT_APP_AGENCY?.toUpperCase()} ${country}`,
    CreatedAt: '2024-08-06 08:22:33',
    LastEditedBy: '',
    LastEditedAt: '',
    __typename: 'ListTemplateResponse',
  } as ListTemplateResponse;

  async function fetchTemplateList() {
    try {
      const response = (await API.graphql({
        query: listTemplates_v2,
        variables: {
          Organization_id: activeOrganization?.OrganizationId,
          /*     ClientCode: activeCampaign?.ClientCode,
                        ClientCountry: activeCampaign?.ClientCountry, */
          Environment: getEnvironment,
          Client: {
            client: activeCampaign?.ClientCode,
            country: activeCampaign?.ClientCountry,
          },
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: ListTemplates_v2Query };

      const newTemplatesList = response.data.listTemplates_v2?.concat(agencyTemplate);
      setTemplatesList(response.data.listTemplates_v2);
      // setTemplatesList(newTemplatesList);
      // console.log("TEMPLATES LIST", response.data.listTemplates_v2)
    } catch (e) {
      console.log('ERROR FETCHING TEMPLATE LIST', e);
    }
  }

  const fetchTemplateById = useCallback(
    async (templateID: any) => {
      setStoredQueries([]);
      try {
        const response = (await API.graphql({
          query: getTemplateById,
          variables: {
            TemplateId: templateID,
            Client: {
              ClientCode: activeCampaign?.ClientCode,
              ClientCountry: activeCampaign?.ClientCountry,
            } as ClientsInput,
          },
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as { data: GetTemplateByIdQuery };

        if (response.data.getTemplateById?.Name) {
          setTemplateName(response.data.getTemplateById?.Name);
        }
        const placeholders = response.data.getTemplateById?.Placeholders;
        if (placeholders) {
          setFetchedPlaceholders(response.data.getTemplateById?.Placeholders as TemplatePlaceholder[]);
        }
        if (response.data.getTemplateById) {
          const { Name, Id } = response.data.getTemplateById;
          const newTemplate = { Name: Name ?? 'unknown', Id: Id ?? 'unknown' };

          setFetchedTemplateData(newTemplate);
        }

        //  setTemplateID(response.data.getTemplateById?.Id)
      } catch (e) {
        console.log('ERROR FETCHING TEMPLATE BY ID', e);
      }
    },
    [activeCampaign, setStoredQueries],
  );

  async function templateUpdateAfterCAllingStoredQ(name: any, placeholders: any) {
    try {
      const response = (await API.graphql({
        query: updateTemplate_v2,
        variables: {
          TemplateId: fetchedTemplateData?.Id,
          TemplateName: name,
          Placeholders: placeholders,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: UpdateTemplate_v2Mutation };
      setSaveTemplateMessage(response.data.updateTemplate_v2?.Message);
      fetchTemplateList();
    } catch (e) {
      console.log('ERROR SAVING TEMPLATE', e);
    }
  }

  async function templateDelete(id: any) {
    try {
      const response = (await API.graphql({
        query: deleteTemplate,
        variables: {
          TemplateId: id,
        },
        authMode: 'AMAZON_COGNITO_USER_POOLS',
      })) as { data: DeleteTemplateMutation };
      setSaveTemplateMessage(response.data.deleteTemplate?.Message);
      /*             setTimeout(() => {
                            setSaveTemplateMessage(undefined)
                        }, 2000) */
      setTemplatesList([]);
      activeOrganization !== null && activeOrganization !== undefined && fetchTemplateList();
    } catch (e) {
      console.log('ERROR SAVING TEMPLATE', e);
    }
  }

  useEffect(() => {
    activeCampaign && activeOrganization !== null && activeOrganization !== undefined && fetchTemplateList();
  }, [activeOrganization]);

  const [isDragEnabled, setIsDragEnabled] = useState(false);

  const toggleDrag = () => {
    setIsDragEnabled(!isDragEnabled);
  };

  return (
    <TemplateContext.Provider
      value={{
        templateSave,
        updateTemplatePlaceholderList,
        templatesList,
        fetchTemplateById,
        fetchedPlaceholders,
        saveTemplateMessage,
        templateName,
        setTemplateName,
        fetchedTemplateData,
        templateUpdate,
        templateDelete,
        toggleDrag,
        isDragEnabled,
      }}
    >
      {props.children}
    </TemplateContext.Provider>
  );
};

export const useTemplateContext = () => {
  const context = useContext(TemplateContext);
  if (context === undefined) {
    throw new Error('context needs to be provided in template context');
  }
  return context;
};
